import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Package, Trash, PencilSimple, Plus, Star, ArrowsClockwise } from "@phosphor-icons/react";
import { AdminShell, Field } from "@/components/AdminShell";
import api, { errDetail } from "@/lib/api";

const EMPTY = { name: "", price_monthly: 0, currency: "usd", seats: 1, features: "", popular: false, active: true, auto_learn_daily_cap: "", monthly_tokens: "", locked_provider: "", locked_model_name: "", audience: "", learn_inbox_count: "", learn_sent_count: "", shared_inboxes: "", priority_inbox: false, priority_scan_count: "", priority_daily_cap: "" };

export default function AdminPackages() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [models, setModels] = useState([]);
  const [modelSource, setModelSource] = useState("live");
  const [modelsLoading, setModelsLoading] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);

  const loadModels = async (provider, force = false) => {
    if (!provider) { setModels([]); return; }
    setModelsLoading(true);
    try {
      const { data } = await api.get("/admin/provider-models", { params: { provider, ...(force ? { t: Date.now() } : {}) } });
      setModels(data.models || []);
      setModelSource(data.source);
      if (force) toast.success(`Loaded ${data.models.length} ${provider} models (${data.source === "live" ? "live" : "price sheet"})`);
    } catch (e) { toast.error(errDetail(e)); setModels([]); }
    setModelsLoading(false);
  };

  const filteredModels = models.filter((m) => m.id.toLowerCase().includes((form.locked_model_name || "").toLowerCase()));

  const load = useCallback(() => api.get("/admin/packages").then((r) => setPackages(r.data)).catch((e) => toast.error(errDetail(e))), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Package needs a name"); return; }
    const body = { ...form, price_monthly: parseFloat(form.price_monthly) || 0, seats: parseInt(form.seats) || 1,
      auto_learn_daily_cap: form.auto_learn_daily_cap === "" || form.auto_learn_daily_cap === null ? null : parseInt(form.auto_learn_daily_cap),
      monthly_tokens: form.monthly_tokens === "" || form.monthly_tokens === null ? null : parseInt(form.monthly_tokens),
      learn_inbox_count: form.learn_inbox_count === "" || form.learn_inbox_count === null ? null : parseInt(form.learn_inbox_count),
      learn_sent_count: form.learn_sent_count === "" || form.learn_sent_count === null ? null : parseInt(form.learn_sent_count),
      locked_model: form.locked_provider && form.locked_model_name ? { provider: form.locked_provider, model: form.locked_model_name } : null,
      audience: form.audience || null,
      shared_inboxes: form.shared_inboxes === "" || form.shared_inboxes === null ? 0 : parseInt(form.shared_inboxes) || 0,
      priority_inbox: !!form.priority_inbox,
      priority_scan_count: form.priority_scan_count === "" || form.priority_scan_count === null ? null : parseInt(form.priority_scan_count),
      priority_daily_cap: form.priority_daily_cap === "" || form.priority_daily_cap === null ? null : parseInt(form.priority_daily_cap),
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean) };
    delete body.locked_provider; delete body.locked_model_name;
    try {
      if (editingId) { await api.put(`/admin/packages/${editingId}`, body); toast.success("Package updated"); }
      else { await api.post("/admin/packages", body); toast.success("Package created"); }
      setForm(EMPTY); setEditingId(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const edit = (p) => { setEditingId(p.id); setForm({ ...p, features: (p.features || []).join("\n"), auto_learn_daily_cap: p.auto_learn_daily_cap ?? "", monthly_tokens: p.monthly_tokens ?? "", learn_inbox_count: p.learn_inbox_count ?? "", learn_sent_count: p.learn_sent_count ?? "", locked_provider: p.locked_model?.provider || "", locked_model_name: p.locked_model?.model || "", audience: p.audience || "", shared_inboxes: p.shared_inboxes ?? "", priority_inbox: !!p.priority_inbox, priority_scan_count: p.priority_scan_count ?? "", priority_daily_cap: p.priority_daily_cap ?? "" }); if (p.locked_model?.provider) loadModels(p.locked_model.provider); };
  const remove = async (p) => {
    try { await api.delete(`/admin/packages/${p.id}`); toast.success("Package deleted"); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <AdminShell title="Packages" subtitle="Create the plans users see on the public landing page. Price 0 = free plan (no Stripe checkout)." testId="admin-packages-page">
      <div className="border border-gray-200 rounded-md p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Package size={18} className="text-blue-600" />
          <h2 className="font-heading font-bold text-gray-900">{editingId ? "Edit package" : "New package"}</h2>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name" testId="pkg-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Pro" />
          <Field label="Price per month (USD)" testId="pkg-price-input" type="number" value={form.price_monthly} onChange={(v) => setForm({ ...form, price_monthly: v })} placeholder="29" />
          <Field label="Seats included" testId="pkg-seats-input" type="number" value={form.seats} onChange={(v) => setForm({ ...form, seats: v })} placeholder="5" />
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Audience (plan group)</label>
            <div className="flex items-center gap-4 pt-2">
              {["", "personal", "team"].map((a) => (
                <label key={a} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input data-testid={`pkg-audience-${a || "auto"}`} type="radio" name="audience" checked={form.audience === a}
                    onChange={() => setForm({ ...form, audience: a })} className="w-4 h-4 accent-blue-600" />
                  {a === "" ? "Auto (by seats)" : a === "personal" ? "Personal" : "Team"}
                </label>
              ))}
            </div>
            {form.audience === "personal" && parseInt(form.seats) > 1 && (
              <p data-testid="pkg-audience-hint" className="text-xs text-amber-600 mt-1">⚠ Personal plans usually have 1 seat</p>
            )}
            {form.audience === "team" && parseInt(form.seats) <= 1 && (
              <p data-testid="pkg-audience-hint" className="text-xs text-amber-600 mt-1">⚠ Team plans usually have 2+ seats</p>
            )}
          </div>
          <Field label="Auto-learn daily cap (empty = Unlimited)" testId="pkg-autolearn-cap-input" type="number" value={form.auto_learn_daily_cap}
            onChange={(v) => setForm({ ...form, auto_learn_daily_cap: v })} placeholder="Unlimited" />
          <Field label="Monthly token pool (empty = Unlimited)" testId="pkg-monthly-tokens-input" type="number" value={form.monthly_tokens}
            onChange={(v) => setForm({ ...form, monthly_tokens: v })} placeholder="Unlimited" />
          <Field label="Shared inboxes included (0 = none)" testId="pkg-shared-inboxes-input" type="number" value={form.shared_inboxes}
            onChange={(v) => setForm({ ...form, shared_inboxes: v })} placeholder="0" />
          <Field label="Learn from last N inbox emails (empty = 100)" testId="pkg-learn-inbox-input" type="number" value={form.learn_inbox_count}
            onChange={(v) => setForm({ ...form, learn_inbox_count: v })} placeholder="100" />
          <Field label="Learn from last N sent emails (empty = 50)" testId="pkg-learn-sent-input" type="number" value={form.learn_sent_count}
            onChange={(v) => setForm({ ...form, learn_sent_count: v })} placeholder="50" />
          <Field label="Priority scan: last N emails (empty = 100)" testId="pkg-priority-scan-input" type="number" value={form.priority_scan_count}
            onChange={(v) => setForm({ ...form, priority_scan_count: v })} placeholder="100" />
          <Field label="Priority classifier daily cap (empty = 200)" testId="pkg-priority-cap-input" type="number" value={form.priority_daily_cap}
            onChange={(v) => setForm({ ...form, priority_daily_cap: v })} placeholder="200" />
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Locked model (optional — users can't change)</label>
            <div className="flex gap-2">
              <select data-testid="pkg-locked-provider-select" value={form.locked_provider}
                onChange={(e) => { setForm({ ...form, locked_provider: e.target.value, locked_model_name: "" }); loadModels(e.target.value); }}
                className="border border-gray-300 rounded-md px-2 py-2 text-sm">
                <option value="">Any model</option>
                <option value="openai">openai</option>
                <option value="anthropic">anthropic</option>
                <option value="openrouter">openrouter</option>
              </select>
              {form.locked_provider && (
                <div className="flex-1 relative">
                  <div className="flex gap-1">
                    <input data-testid="pkg-locked-model-input" value={form.locked_model_name}
                      onChange={(e) => { setForm({ ...form, locked_model_name: e.target.value }); setModelOpen(true); }}
                      onFocus={() => setModelOpen(true)}
                      onBlur={() => setTimeout(() => setModelOpen(false), 180)}
                      placeholder={modelsLoading ? "Loading live models…" : "Type to search models…"}
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm" autoComplete="off" />
                    <button type="button" data-testid="pkg-models-refresh-btn" title="Refresh live model list"
                      onClick={() => loadModels(form.locked_provider, true)}
                      className="border border-gray-300 rounded-md px-2 text-gray-500 hover:text-blue-600 hover:border-blue-300">
                      <ArrowsClockwise size={14} className={modelsLoading ? "animate-spin" : ""} />
                    </button>
                  </div>
                  {modelOpen && filteredModels.length > 0 && (
                    <div data-testid="pkg-model-dropdown"
                      className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                      {filteredModels.slice(0, 60).map((m) => (
                        <button type="button" key={m.id} data-testid={`pkg-model-option-${m.id}`}
                          onMouseDown={() => { setForm((f) => ({ ...f, locked_model_name: m.id })); setModelOpen(false); }}
                          className="w-full text-left px-3 py-1.5 text-xs hover:bg-blue-50 flex justify-between gap-2">
                          <span className="text-gray-800 truncate">{m.id}</span>
                          {m.price && <span className="text-gray-400 shrink-0">{m.price}</span>}
                        </button>
                      ))}
                      <div className="px-3 py-1.5 text-[10px] text-gray-400 border-t border-gray-100">
                        {models.length} models · {modelSource === "live" ? "fetched live from provider" : "from price sheet (live fetch unavailable)"} · custom names allowed
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 block mb-1">Flags</label>
            <div className="flex items-center gap-4 pt-2">
              <label className="flex items-center gap-1.5 text-sm text-gray-700">
                <input data-testid="pkg-popular-toggle" type="checkbox" checked={form.popular} onChange={(e) => setForm({ ...form, popular: e.target.checked })} className="w-4 h-4 accent-blue-600" />
                <Star size={13} className="text-amber-500" /> Popular
              </label>
              <label className="flex items-center gap-1.5 text-sm text-gray-700">
                <input data-testid="pkg-active-toggle" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-blue-600" />
                Active (visible on landing)
              </label>
              <label className="flex items-center gap-1.5 text-sm text-gray-700">
                <input data-testid="pkg-priority-toggle" type="checkbox" checked={!!form.priority_inbox} onChange={(e) => setForm({ ...form, priority_inbox: e.target.checked })} className="w-4 h-4 accent-blue-600" />
                ⚡ Priority inbox included
              </label>
            </div>
          </div>
          <div className="col-span-2">
            <label className="text-xs font-medium text-gray-600 block mb-1">Features (one per line)</label>
            <textarea data-testid="pkg-features-input" rows={4} value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })}
              placeholder={"5 team seats\nFull AI Email Agent\nAPI access"}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button data-testid="pkg-save-btn" onClick={save}
            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
            <Plus size={14} /> {editingId ? "Update package" : "Create package"}
          </button>
          {editingId && (
            <button data-testid="pkg-cancel-edit-btn" onClick={() => { setEditingId(null); setForm(EMPTY); }}
              className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2">Cancel</button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {packages.map((p) => (
          <div key={p.id} data-testid={`pkg-row-${p.id}`} className="border border-gray-200 rounded-md p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold text-gray-900">{p.name}</span>
                {p.popular && <span className="text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">POPULAR</span>}
                {!p.active && <span className="text-[10px] font-bold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">HIDDEN</span>}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">${p.price_monthly}/mo · {p.seats} seat{p.seats > 1 ? "s" : ""} · <span className={`font-bold ${p.audience === "team" || (!p.audience && p.seats > 1) ? "text-purple-600" : "text-blue-600"}`}>{(p.audience || (p.seats > 1 ? "team" : "personal")).toUpperCase()}</span> · {(p.features || []).length} features · learn cap {p.auto_learn_daily_cap ?? "∞"}/day · pool {p.monthly_tokens ? p.monthly_tokens.toLocaleString() : "∞"} tokens{p.locked_model ? ` · 🔒 ${p.locked_model.model}` : ""}</div>
            </div>
            <button data-testid={`pkg-edit-${p.id}`} onClick={() => edit(p)} className="text-gray-400 hover:text-blue-600 transition-colors p-1.5"><PencilSimple size={16} /></button>
            <button data-testid={`pkg-delete-${p.id}`} onClick={() => remove(p)} className="text-gray-400 hover:text-red-600 transition-colors p-1.5"><Trash size={16} /></button>
          </div>
        ))}
      </div>
      <TokenPacks />
    </AdminShell>
  );
}

function TokenPacks() {
  const [packs, setPacks] = useState([]);
  const [pForm, setPForm] = useState({ name: "", tokens: "", price_usd: "" });
  const load = useCallback(() => api.get("/admin/token-packages").then((r) => setPacks(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/token-packages", { name: pForm.name, tokens: parseInt(pForm.tokens), price_usd: parseFloat(pForm.price_usd) });
      toast.success("Token pack created"); setPForm({ name: "", tokens: "", price_usd: "" }); load();
    } catch (err) { toast.error(errDetail(err)); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete token pack "${p.name}"?`)) return;
    try { await api.delete(`/admin/token-packages/${p.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errDetail(e)); }
  };
  return (
    <div className="mt-8 border-t border-gray-200 pt-6" data-testid="token-packs-section">
      <h2 className="font-heading font-bold text-gray-900 mb-1">Token top-up packs</h2>
      <p className="text-xs text-gray-500 mb-3">One-time Stripe purchases (separate from subscriptions). Purchased tokens expire at the end of the buyer's billing period.</p>
      <form onSubmit={add} className="flex flex-wrap gap-2 mb-4">
        <input data-testid="pack-name-input" required placeholder="Booster" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-36" />
        <input data-testid="pack-tokens-input" required type="number" placeholder="10000 tokens" value={pForm.tokens} onChange={(e) => setPForm({ ...pForm, tokens: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-36" />
        <input data-testid="pack-price-input" required type="number" step="0.01" placeholder="$15" value={pForm.price_usd} onChange={(e) => setPForm({ ...pForm, price_usd: e.target.value })} className="border border-gray-300 rounded-md px-3 py-2 text-sm w-28" />
        <button data-testid="pack-add-btn" className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-semibold"><Plus size={14} /> New pack</button>
      </form>
      <div className="space-y-2">
        {packs.map((p) => (
          <div key={p.id} data-testid={`pack-row-${p.id}`} className="flex items-center gap-3 border border-gray-200 rounded-md px-4 py-2.5 text-sm">
            <span className="font-semibold text-gray-900">{p.name}</span>
            <span className="text-gray-500">{p.tokens.toLocaleString()} tokens — ${p.price_usd}</span>
            <span className={`text-[10px] uppercase font-bold rounded px-1.5 py-px ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{p.active ? "active" : "off"}</span>
            <button data-testid={`pack-delete-${p.id}`} onClick={() => remove(p)} className="ml-auto text-gray-400 hover:text-red-600 p-1"><Trash size={15} /></button>
          </div>
        ))}
        {packs.length === 0 && <p className="text-sm text-gray-400">No token packs yet.</p>}
      </div>
    </div>
  );
}
